import React from "react";
import {
    capitalize,
    Chip,
    Alert,
} from "@mui/material";
import {Toolbar, ToolbarButton} from "@mui/x-data-grid";
import {
    FilterPanelTrigger,
    gridColumnLookupSelector,
    GridFilterListIcon,
    useGridApiContext,
    useGridSelector,
    ColumnsPanelTrigger,
} from "@mui/x-data-grid-premium";
import {observer} from "mobx-react-lite";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import {Refresh, ViewColumn, WidthWide} from "@mui/icons-material";
import {useStores} from "../../../../stores";
import {
    CAT_EQ_OPERATOR, CAT_NEQ_OPERATOR
} from "../../../../pages/categorization/components/review-table/review-table-muix/filters/CategoryFilter.tsx";

/**
 * Taken from https://mui.com/x/react-data-grid/components/toolbar/#filter-bar
 */
export const SCToolbar: React.FC = observer(() => {
    const {spendConcentrationStore} = useStores();
    const apiRef = useGridApiContext();
    const tableController = spendConcentrationStore.sCPageController.tableController;
    const filterController = spendConcentrationStore.sCPageController.filterController;
    const columns = useGridSelector(apiRef, gridColumnLookupSelector);

    return <Toolbar>
        <Tooltip title="Filters">
            <FilterPanelTrigger render={<ToolbarButton/>}>
                <GridFilterListIcon fontSize="small"/>
            </FilterPanelTrigger>
        </Tooltip>

        <Stack direction="row" sx={{gap: 0.5, flex: 1}}>
            {filterController.gridFilterModel.items.filter(f => f?.value).map((filter) => {
                const column = columns[filter.field];
                const fieldName = column?.headerName ?? filter.field;

                let operator = '';
                let label: string;

                if (filter.operator === CAT_EQ_OPERATOR.operatorValue) {
                    // noinspection TypeScriptUnresolvedReference
                    operator = apiRef.current.getLocaleText('filterOperatorEquals');
                    label = `${fieldName} ${operator} previous`;
                } else if (filter.operator === CAT_NEQ_OPERATOR.operatorValue) {
                    // noinspection TypeScriptUnresolvedReference
                    operator = apiRef.current.getLocaleText('filterOperatorDoesNotEqual');
                    label = `${fieldName} ${operator} previous`;
                } else if (filter.operator === 'between') {
                    // To avoid missing translation
                    // See https://github.com/mui/mui-x/issues/13217
                    const lowerBound = String(Number(filter.value?.at(0) ?? 0) * 100) + ' <= ';
                    const upperBound = ' <= ' + String(Number(filter.value?.at(1) ?? 0) * 100);
                    label = `${filter.value?.at(0) ? lowerBound : ''}${fieldName}${filter.value?.at(1) ? upperBound : ''}`;
                } else {
                    const lookup = `filterOperator${capitalize(filter.operator)}` as 'filterOperatorContains';
                    // noinspection TypeScriptUnresolvedReference
                    operator = apiRef.current.getLocaleText(lookup);
                    label = `${fieldName} ${operator} ${filter.value ?? ''}`;
                }

                return <Chip
                    key={filter.id}
                    label={label}
                    onDelete={() => filterController.removeFilter(filter.id)}
                    className="ms-1"
                />;
            })}
        </Stack>

        {(tableController.sortModel.length === 0) && <Alert severity="info">
            No sorting is specified!&nbsp;
            {((tableController.defaultSortModel?.length ?? 0) > 0) && <span>Default is
                {tableController.defaultSortModel?.map(si => <b>
                    {tableController.tableColumns.find(c => c.field === si.field)?.headerName ?? ''}
                    &nbsp;
                    {si.sort === 'desc' ? 'Descending' : 'Ascending'}
                </b>)}
            </span>}
        </Alert>}

        <Tooltip title="Auto size columns">
            <ToolbarButton onClick={() => tableController.setAutoResize(!tableController.autoResize)}>
                <WidthWide fontSize="small" color={tableController.autoResize ? 'primary' : 'disabled'}/>
            </ToolbarButton>
        </Tooltip>

        <ColumnsPanelTrigger aria-label="sc-column-panel-trigger" render={<ToolbarButton/>}>
            <ViewColumn fontSize="small"/>
        </ColumnsPanelTrigger>

        <Tooltip title="Refresh data">
            <ToolbarButton onClick={() => tableController.resetRowState(true)}>
                <Refresh fontSize="small"/>
            </ToolbarButton>
        </Tooltip>
    </Toolbar>;
});